// Full-screen panel listing the in-stock IMEIs/serials for one variant in one
// warehouse. Opened by clicking a stock row's quantity. Each serial opens its
// full history; the whole list can be exported to Excel (CSV, UTF-8 BOM).
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Loader2, Download, Search } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { SerialHistoryModal } from "./serial-history-modal";

interface SerialRow {
  id: string;
  serialNo: string;
  status: string;
}

const STATUS_LABELS: Record<string, string> = {
  imported: "Nhập về", in_stock: "Tồn kho", sold: "Đã bán", in_repair: "Đang sửa",
  rma_to_vendor: "RMA nhà cung cấp", defective: "Hỏng", returned: "Hoàn trả",
};

interface Props {
  variantId: string;
  warehouseId: string;
  productName?: string | null;
  sku?: string | null;
  warehouseName?: string | null;
  onClose: () => void;
}

export function StockSerialsModal({ variantId, warehouseId, productName, sku, warehouseName, onClose }: Props) {
  const [historySerial, setHistorySerial] = useState<string | null>(null);
  const [q, setQ] = useState("");

  const { data: serials = [], isLoading } = useQuery({
    queryKey: ["stock-serials", variantId, warehouseId],
    queryFn: () =>
      apiClient
        .get<{ data: SerialRow[] }>("/v1/serials", {
          params: { variant_id: variantId, warehouse_id: warehouseId, status: "in_stock", limit: 500 },
        })
        .then((r) => r.data.data),
  });

  const filtered = q.trim()
    ? serials.filter((s) => s.serialNo.toLowerCase().includes(q.trim().toLowerCase()))
    : serials;

  function exportExcel() {
    const header = ["STT", "IMEI / Serial", "SKU", "Tên sản phẩm", "Kho", "Trạng thái"];
    const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [header.map(esc).join(",")];
    filtered.forEach((s, i) => {
      lines.push([i + 1, s.serialNo, sku ?? "", productName ?? "", warehouseName ?? "", STATUS_LABELS[s.status] ?? s.status].map(esc).join(","));
    });
    // ﻿ (BOM) so Excel reads UTF-8 Vietnamese correctly; CRLF line endings.
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `IMEI-ton-kho-${sku || "san-pham"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 border-b px-4 py-3 sm:px-6">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">IMEI tồn kho</h2>
          <p className="truncate text-sm text-muted-foreground">
            {productName ?? "—"}
            {sku ? <span className="ml-1 font-mono text-xs">({sku})</span> : null}
            {warehouseName ? <span className="ml-2">· Kho: {warehouseName}</span> : null}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportExcel} disabled={filtered.length === 0}>
            <Download size={15} className="mr-1" /> Xuất Excel
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose}><X size={18} /></Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 border-b px-4 py-2 sm:px-6">
        <div className="relative min-w-[220px] max-w-sm flex-1">
          <Search size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Lọc nhanh trong danh sách…"
            className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <span className="text-sm text-muted-foreground">
          {filtered.length}{q.trim() ? `/${serials.length}` : ""} IMEI
        </span>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-3 sm:px-6">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 size={18} className="animate-spin" /> Đang tải…
          </div>
        ) : serials.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground">
            Không có IMEI tồn kho (sản phẩm này không quản lý theo serial, hoặc chưa nhập IMEI).
          </p>
        ) : (
          <Table>
            <THead>
              <Tr>
                <Th className="w-12 text-right">#</Th>
                <Th>IMEI / Serial</Th>
                <Th>Trạng thái</Th>
                <Th className="text-right">Thao tác</Th>
              </Tr>
            </THead>
            <TBody>
              {filtered.map((s, i) => (
                <Tr key={s.id}>
                  <Td className="text-right text-muted-foreground">{i + 1}</Td>
                  <Td className="font-mono text-sm">{s.serialNo}</Td>
                  <Td>
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950/40 dark:text-green-300">
                      {STATUS_LABELS[s.status] ?? s.status}
                    </span>
                  </Td>
                  <Td className="text-right">
                    <button
                      type="button"
                      onClick={() => setHistorySerial(s.serialNo)}
                      className="text-sm text-primary hover:underline"
                    >
                      Xem lịch sử →
                    </button>
                  </Td>
                </Tr>
              ))}
              {filtered.length === 0 && (
                <Tr>
                  <Td colSpan={4} className="text-center text-muted-foreground">Không có IMEI khớp bộ lọc</Td>
                </Tr>
              )}
            </TBody>
          </Table>
        )}
      </div>

      {historySerial && (
        <SerialHistoryModal serialNo={historySerial} onClose={() => setHistorySerial(null)} />
      )}
    </div>
  );
}
